import React, { useState, useEffect, useRef } from 'react';
import { Upload, X, PaperclipIcon, Trash2, Download } from 'lucide-react';
import { validateFiles, formatFileSize } from './fileValidationUtils';
import ManageRaidLogAssignees from './ManageRaidLogAssignees';

// Import the JSON-based configuration
import appConfig, { ENDPOINT_KEYS } from './config/appConfig';

/**
 * Create RAID Log Dialog Component with Frozen Header/Footer
 */
const CreateRaidLogDialog = ({
  corporateAccount,
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  newRaidLog,
  handleInputChange,
  raidTypeOptions,
  statusOptions,
  priorityOptions,
  criticalityOptions,
  currentUser,
  formError,
  setSelectedUserId,
  isEditMode,
  currentProject,
  canCreateOrEditRaidLog
}) => {
  const [userSearchText, setUserSearchText] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const dropdownRef = useRef(null);
  const textareaRef = useRef(null);
  const login_token = localStorage.getItem('authToken');

  const [selectedUser, setSelectedUser] = useState(null);
  const [createSuccess, setCreateSuccess] = useState(null);

  // State for attachments
  const [files, setFiles] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState(false);
  const [attachmentUploadFailed, setAttachmentUploadFailed] = useState(false);
  const fileInputRef = useRef(null);

  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [error, setError] = useState('');


// Add these state variables for manage assignees functionality
const [isManageAssigneesDialogOpen, setIsManageAssigneesDialogOpen] = useState(false);
const [selectedRaidLogType, setSelectedRaidLogType] = useState('');
const [selectedRaidLogIdWithPrefix, setSelectedRaidLogIdWithPrefix] = useState('');


  // Fixed dialog styles with frozen header/footer
  const dialogStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    dialog: {
      backgroundColor: 'white',
      borderRadius: '8px',
      width: createSuccess ? '450px' : '700px',
      maxWidth: '90vw',
      height: '90vh',
      maxHeight: '90vh',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    header: {
      backgroundColor: '#CCCCFF',
      textAlign: 'center',
      padding: '10px 20px',
      borderRadius: '8px 8px 0 0',
      fontSize: '18px',
      fontWeight: 'bold',
      flexShrink: 0
    },
    content: {
      flex: 1,
      overflowY: 'auto',
      padding: '20px',
      maxHeight: 'calc(90vh - 120px)'
    },
    footer: {
      padding: '15px 20px',
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#f9fafb',
      borderRadius: '0 0 8px 8px',
      flexShrink: 0
    }
  };

  // Focus management
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen]);

  // Fetch users list on component mount
  useEffect(() => {
    if (isOpen) {
      if (!isEditMode) {
        setUserSearchText('');
        setSelectedUser(null);
        setFiles([]);
        setAttachments([]);
        setAttachmentUploadFailed(false);
      }
      fetchUsersList();
    }
  }, [isOpen, isEditMode]);

  useEffect(() => {
    if (isOpen && isEditMode && newRaidLog) {
      // Only set initial values, don't override if user has already made changes
      if (newRaidLog.logged_by_user_name && !selectedUser) {
        setUserSearchText(newRaidLog.logged_by_user_name);
        if (newRaidLog.raid_logged_by_user) {
          setSelectedUser({
            user_id: newRaidLog.raid_logged_by_user,
            user_name: newRaidLog.logged_by_user_name
          });
        }
      }
    }
  }, [isOpen, isEditMode, newRaidLog, selectedUser]);

  useEffect(() => {
    if (isOpen && isEditMode && newRaidLog?.raid_id) {
      fetchAttachments(newRaidLog.raid_type, newRaidLog.raid_id);
    }
  }, [isOpen, isEditMode, newRaidLog]);

  // Handle clicks outside of dropdown to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter users based on search text
  useEffect(() => {
    if (userSearchText) {
      const lowercaseSearchText = userSearchText.toLowerCase();
      const filtered = usersList.filter(
        user =>
          user.user_name.toLowerCase().includes(lowercaseSearchText) ||
          user.user_id.toLowerCase().includes(lowercaseSearchText)
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers([]);
    }
  }, [userSearchText, usersList]);

  useEffect(() => {
    if (isOpen) {
      // Reset success state when dialog opens
      setCreateSuccess(null);
      setShowValidationErrors(false);
      setError('');
    }
  }, [isOpen]);

  // Sync formError from parent
  useEffect(() => {
    if (formError) {
      setError(formError);
    }
  }, [formError]);

  // Fetch users list from API
  const fetchUsersList = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_user_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          "corporate_account": corporateAccount,
          "status": "Active"
        }),
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setUsersList(data.user_list);
      } else {
        console.error('Failed to fetch users list:', data.status_description);
      }
    } catch (error) {
      console.error('Error fetching users list:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Handle user selection
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    if (setSelectedUserId) {
      setSelectedUserId(user.user_id);
    }
    setUserSearchText(user.user_name);
    setIsDropdownOpen(false);
  };

  // Handle user search input change
  const handleUserSearchChange = (e) => {
    console.log('User search changed to:', e.target.value);
    setUserSearchText(e.target.value);
    setIsDropdownOpen(true);

    // Only clear selected user if the input is completely empty
    if (!e.target.value) {
      console.log('Clearing selected user because input is empty');
      setSelectedUser(null);
      if (setSelectedUserId) {
        setSelectedUserId('');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  // Fetch attachments for existing RAID log
  const fetchAttachments = async (raidType, raidId) => {
    if (!raidId) return;

    setIsLoadingAttachments(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_requirement_attachments'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          req_id: `RAID-${raidId}`
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setAttachments(data.attachments || []);
      } else {
        console.error('Failed to fetch attachments:', data.status_description);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setIsLoadingAttachments(false);
    }
  };



// Add this function after your existing handler functions
const handleManageAssigneesClick = () => {
  if (!isEditMode || !newRaidLog.raid_id) {
    setError('Please save the RAID log first before managing assignees');
    return;
  }

  // Set the raid log details for the assignees dialog
  setSelectedRaidLogType(newRaidLog.raid_type);
  setSelectedRaidLogIdWithPrefix(newRaidLog.raid_id_with_prefix);
  setIsManageAssigneesDialogOpen(true);
};



  // Handle file input change with IMMEDIATE validation
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // IMMEDIATE validation using your existing validateFiles function
    const validationResult = validateFiles(selectedFiles);

    if (!validationResult.isValid) {
      setError(`File validation failed: ${validationResult.errors.join('; ')}`);
      e.target.value = ''; // Clear the file input
      return;
    }

    setError(''); // Clear any previous errors
    setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
  };

  // Remove a file from the list of files to upload
  const removeFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  // Delete an existing attachment
  const handleDeleteAttachment = async (attachmentId) => {
    try {
      const response = await fetch(appConfig.getUrl('/delete_requirement_attachment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          attachment_id: attachmentId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setAttachments(prevAttachments =>
          prevAttachments.filter(attachment => attachment.attachment_id !== attachmentId)
        );
      } else {
        setError(`Failed to delete attachment: ${data.status_description}`);
      }
    } catch (error) {
      setError(`Error deleting attachment: ${error.message}`);
    }
  };

  // Download an attachment
  const handleDownloadAttachment = (attachmentId) => {
    const downloadUrl = appConfig.getUrl(`/download_requirement_attachment/${attachmentId}`);

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = downloadUrl;
    form.target = '_blank';

    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'token';
    tokenInput.value = login_token;
    form.appendChild(tokenInput);

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  // Upload attachments for a RAID log
  const uploadAttachments = async (raidType, raidId) => {
    if (files.length === 0) return true;

    // Additional validation before upload
    const validationResult = validateFiles(files);
    if (!validationResult.isValid) {
      setError(`File validation failed: ${validationResult.errors.join(', ')}`);
      return false;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('req_id', `RAID-${raidId}`);
    formData.append('corporate_account', corporateAccount);
    formData.append('project_id', currentProject);
    formData.append('user_id', localStorage.getItem('user_id') || '');

    files.forEach(file => {
      formData.append('files[]', file);
    });

    try {
      const response = await fetch(appConfig.getUrl('/upload_requirement_attachment'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${login_token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.status === 'Success') {
        setFiles([]);
        if (isEditMode) {
          fetchAttachments(newRaidLog.raid_type, raidId);
        }
        return true;
      } else {
        throw new Error(data.status_description || 'Failed to upload attachments');
      }
    } catch (error) {
      console.error('Error uploading attachments:', error);

      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        setError('Network error: Please check your internet connection and try again. Please close this dialog and edit the RAID log to retry uploading attachments.');
      } else if (error.message.includes('413') || error.message.includes('too large')) {
        setError('RAID log saved, but file not attached. Please reduce file size and try again (Maximum: 10MB per file). Please close this dialog and edit the RAID log to retry uploading attachments.');
      } else {
        setError(`Error uploading attachments: ${error.message}. Please close this dialog and edit the RAID log to retry uploading attachments.`);
      }

      return false;
    } finally {
      setIsUploading(false);
    }
  };

  // Attachment List Component
  const AttachmentList = ({ attachments, onDelete, onDownload, isLoading }) => {
    if (isLoading) {
      return <div style={{ marginTop: '8px', fontSize: '14px', color: '#6b7280' }}>Loading attachments...</div>;
    }

    if (!attachments || attachments.length === 0) {
      return <div style={{ marginTop: '8px', fontSize: '14px', color: '#6b7280' }}>No attachments</div>;
    }

    return (
      <div style={{ marginTop: '8px' }}>
        {attachments.map(attachment => (
          <div
            key={attachment.attachment_id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px',
              backgroundColor: '#f9fafb',
              borderRadius: '4px',
              border: '1px solid #d1d5db',
              marginBottom: '8px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <PaperclipIcon style={{ height: '16px', width: '16px', marginRight: '8px', color: '#6b7280' }} />
              <span style={{ fontSize: '14px', marginRight: '8px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {attachment.file_name}
              </span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                ({formatFileSize(attachment.file_size)})
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => onDownload(attachment.attachment_id)}
                style={{
                  padding: '4px',
                  color: '#2563eb',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <Download style={{ height: '16px', width: '16px' }} />
              </button>
              <button
                type="button"
                onClick={() => onDelete(attachment.attachment_id)}
                style={{
                  padding: '4px',
                  color: '#dc2626',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <Trash2 style={{ height: '16px', width: '16px' }} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Handle final form submission
  const handleFormSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    setShowValidationErrors(true);
    setError('');

    // Validation checks
    if (!newRaidLog.raid_description?.trim()) {
      setError('Description is required');
      return;
    }
    if (!newRaidLog.raid_type) {
      setError('RAID type is required');
      return;
    }
    if (!newRaidLog.status) {
      setError('Status is required');
      return;
    }
    if (!newRaidLog.criticality) {
      setError('Criticality is required');
      return;
    }
    if (!selectedUser) {
      setError('Logged by user is required');
      return;
    }

    if (selectedUser && setSelectedUserId) {
      setSelectedUserId(selectedUser.user_id);
    }

    try {
      const result = await onSubmit();

      if (result && result.success) {
        // Handle file uploads if there are any
        if (files.length > 0 && result.raidId) {
          const uploadSuccess = await uploadAttachments(result.raidType, result.raidId);
          if (!uploadSuccess) {
            setAttachmentUploadFailed(true);
            // Still set success state to trigger list refresh when dialog closes
            setTimeout(() => {
              setCreateSuccess({
                ...result,
                attachmentCount: 0
              });
            }, 10);
            return result;
          }
        }

        // Show success message
        setError(`RAID log ${isEditMode ? 'updated' : 'created'} successfully`);

        // Set success state
        setTimeout(() => {
          setCreateSuccess({
            ...result,
            attachmentCount: files.length
          });
        }, 10);
      }

      return result;
    } catch (err) {
      setError(err.message || 'Error saving RAID log');
      return { success: false };
    }
  };

  const handleCreateNew = () => {
    // Reset all form fields to initial state
    setUserSearchText('');
    setSelectedUser(null);
    setFiles([]);
    setAttachments([]);
    setError('');
    setCreateSuccess(null);
    setShowValidationErrors(false);
    setAttachmentUploadFailed(false);

    if (setSelectedUserId) {
      setSelectedUserId('');
    }

    // Focus on the description field
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);
  };

  if (!isOpen) return null;

  return (
    <div style={dialogStyles.overlay}>
      <div style={dialogStyles.dialog}>
        {/* FROZEN Header */}
        <div style={dialogStyles.header}>
          {createSuccess ?
            `${createSuccess.isEditMode ? 'Update' : 'Create New'} Raid Log Entry` :
            (isEditMode ? `Edit RAID Log Entry ${newRaidLog.raid_id_with_prefix || ''}` : 'Create New RAID Log Entry')
          }
        </div>

        {/* Scrollable Content */}
        <div style={dialogStyles.content}>
          {createSuccess ? (
            // Success message
            <div style={{ marginBottom: '15px' }}>
              <div style={{
                padding: '15px',
                backgroundColor: '#d4edda',
                border: '1px solid #c3e6cb',
                color: '#155724',
                borderRadius: '4px',
                marginBottom: '15px'
              }}>
                <p style={{ fontWeight: '500', margin: '0 0 8px 0' }}>
                  RAID Log successfully {createSuccess.isEditMode ? 'updated' : 'created'}!
                </p>
                <p style={{ margin: 0 }}>
                  RAID Log ID: <span style={{ fontWeight: 'bold' }}>
                    {createSuccess.raidIdWithPrefix || `RAID-${createSuccess.raidId}`}
                  </span>
                </p>
                {createSuccess.attachmentCount > 0 && (
                  <p style={{ margin: '8px 0 0 0' }}>
                    {createSuccess.attachmentCount} attachment{createSuccess.attachmentCount !== 1 ? 's' : ''} uploaded
                  </p>
                )}
              </div>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1 }}>
                {/* ===== BASIC INFORMATION SECTION ===== */}
                <div style={{
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #e0f2fe',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: '#0284c7',
                    borderBottom: '2px solid #0284c7',
                    paddingBottom: '4px'
                  }}>
                    📋 Basic Information
                  </h3>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                    {/* RAID Type */}
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                        RAID Type: <span style={{ color: 'red' }}>*</span>
                      </label>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={newRaidLog.raid_type}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: '#f5f5f5',
                            color: '#666'
                          }}
                          disabled
                          readOnly
                        />
                      ) : (
                        <select
                          name="raid_type"
                          value={newRaidLog.raid_type}
                          onChange={handleInputChange}
                          style={{
                            width: '100%',
                            padding: '8px',
                            border: showValidationErrors && !newRaidLog.raid_type ? '1px solid red' : '1px solid #ddd',
                            borderRadius: '4px'
                          }}
                          required
                        >
                          <option value="">Select RAID Type</option>
                          {raidTypeOptions.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      )}
                      {showValidationErrors && !newRaidLog.raid_type && !isEditMode && (
                        <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          RAID Type is required
                        </span>
                      )}
                    </div>

                    {/* Status */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                        Status<span style={{ color: 'red' }}>*</span>:
                      </label>
                      <select
                        name="status"
                        value={newRaidLog.status}
                        onChange={handleInputChange}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: showValidationErrors && !newRaidLog.status ? '1px solid red' : '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                        required
                      >
                        <option value="">Select Status</option>
                        {statusOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {showValidationErrors && !newRaidLog.status && (
                        <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          Status is required
                        </span>
                      )}
                    </div>

                    {/* Priority */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                        Priority<span style={{ color: 'red' }}>*</span>:
                      </label>
                      <select
                        name="priority"
                        value={newRaidLog.priority}
                        onChange={handleInputChange}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: showValidationErrors && !newRaidLog.priority ? '1px solid red' : '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                        required
                      >
                        <option value="">Select Priority</option>
                        {priorityOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {showValidationErrors && !newRaidLog.priority && (
                        <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          Priority is required
                        </span>
                      )}
                    </div>

                    {/* Criticality */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                        Criticality<span style={{ color: 'red' }}>*</span>:
                      </label>
                      <select
                        name="criticality"
                        value={newRaidLog.criticality}
                        onChange={handleInputChange}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: showValidationErrors && !newRaidLog.criticality ? '1px solid red' : '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                        required
                      >
                        <option value="">Select Criticality</option>
                        {criticalityOptions.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {showValidationErrors && !newRaidLog.criticality && (
                        <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                          Criticality is required
                        </span>
                      )}
                    </div>
                  </div>

                  {/* User Selection with Typeahead */}
                  <div style={{ position: 'relative', marginBottom: '15px' }} ref={dropdownRef}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      Logged By<span style={{ color: 'red' }}>*</span>:
                    </label>
                    <input
                      type="text"
                      value={userSearchText}
                      onChange={handleUserSearchChange}
                      onFocus={() => setIsDropdownOpen(true)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: showValidationErrors && !selectedUser ? '1px solid red' : '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                      placeholder="Search for a user..."
                      required
                    />
                    {showValidationErrors && !selectedUser && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Logged by user is required
                      </span>
                    )}
                    {isDropdownOpen && (
                      <div style={{
                        position: 'absolute',
                        zIndex: 10,
                        marginTop: '4px',
                        width: '100%',
                        backgroundColor: 'white',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        maxHeight: '240px',
                        borderRadius: '4px',
                        overflow: 'auto',
                        border: '1px solid #d1d5db'
                      }}>
                        {isLoadingUsers ? (
                          <div style={{ textAlign: 'center', padding: '8px', color: '#6b7280' }}>Loading users...</div>
                        ) : filteredUsers.length > 0 ? (
                          filteredUsers.map((user) => (
                            <div
                              key={user.user_id}
                              style={{
                                padding: '16px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f3f4f6'
                              }}
                              onClick={() => handleUserSelect(user)}
                              onMouseOver={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                              onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                            >
                              <div style={{ fontWeight: '500' }}>{user.user_name}</div>
                              <div style={{ fontSize: '14px', color: '#6b7280' }}>{user.user_id}</div>
                            </div>
                          ))
                        ) : userSearchText ? (
                          <div style={{ textAlign: 'center', padding: '8px', color: '#6b7280' }}>No users found</div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '8px', color: '#6b7280' }}>Start typing to search users</div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Due Date */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Due Date:</label>
                    <input
                      type="date"
                      name="due_date"
                      value={newRaidLog.due_date ? (() => {
                        const date = new Date(newRaidLog.due_date);
                        const year = date.getUTCFullYear();
                        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                        const day = String(date.getUTCDate()).padStart(2, '0');
                        return `${year}-${month}-${day}`;
                      })() : ''}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      Description<span style={{ color: 'red' }}>*</span>:
                    </label>
                    <textarea
                      ref={textareaRef}
                      name="raid_description"
                      value={newRaidLog.raid_description}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '8px',
                        border: showValidationErrors && !newRaidLog.raid_description ? '1px solid red' : '1px solid #ddd',
                        borderRadius: '4px',
                        minHeight: '80px',
                        resize: 'vertical'
                      }}
                      placeholder="Enter RAID description"
                      required
                    />
                    {showValidationErrors && !newRaidLog.raid_description && (
                      <span style={{ color: 'red', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                        Description is required
                      </span>
                    )}
                  </div>

                  {/* Resolution & Comments */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Resolution:</label>
                      <textarea
                        name="resolution"
                        value={newRaidLog.resolution}
                        onChange={handleInputChange}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          height: '80px',
                          resize: 'vertical'
                        }}
                        placeholder="Enter resolution details if any"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Comments:</label>
                      <textarea
                        name="comments"
                        value={newRaidLog.comments}
                        onChange={handleInputChange}
                        style={{
                          width: '100%',
                          padding: '8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          height: '80px',
                          resize: 'vertical'
                        }}
                        placeholder="Enter any additional comments"
                      />
                    </div>
                  </div>
                </div>

                {/* ===== ATTACHMENTS SECTION ===== */}
                <div style={{
                  backgroundColor: '#faf5ff',
                  border: '1px solid #e9d5ff',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px'
                }}>
                  <h3 style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: '#a855f7',
                    borderBottom: '2px solid #a855f7',
                    paddingBottom: '4px'
                  }}>
                    📎 Attachments
                  </h3>

                  {/* Existing Attachments */}
                  {isEditMode && (
                    <div style={{ marginBottom: '12px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>Current Attachments:</h4>
                      <AttachmentList
                        attachments={attachments}
                        onDelete={handleDeleteAttachment}
                        onDownload={handleDownloadAttachment}
                        isLoading={isLoadingAttachments}
                      />
                    </div>
                  )}

                  {/* File Upload */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <label style={{ fontSize: '14px', fontWeight: '500', color: '#7c3aed' }}>
                        Upload New Attachments:
                      </label>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          fontSize: '14px',
                          color: '#2563eb',
                          backgroundColor: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        <Upload style={{ height: '16px', width: '16px', marginRight: '4px' }} />
                        Select Files
                      </button>
                    </div>

                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                      multiple
                    />

                    {/* Selected Files List */}
                    {files.length > 0 && (
                      <div style={{ marginTop: '8px' }}>
                        {files.map((file, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '8px',
                              backgroundColor: '#dbeafe',
                              borderRadius: '4px',
                              border: '1px solid #93c5fd',
                              marginBottom: '8px'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <PaperclipIcon style={{ height: '16px', width: '16px', marginRight: '8px', color: '#2563eb' }} />
                              <span style={{ fontSize: '14px', marginRight: '8px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {file.name}
                              </span>
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                ({formatFileSize(file.size)})
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeFile(index)}
                              style={{
                                padding: '4px',
                                color: '#dc2626',
                                backgroundColor: 'transparent',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              <X style={{ height: '16px', width: '16px' }} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <p style={{ marginTop: '4px', fontSize: '12px', color: '#6b7280' }}>
                      Supported file types: PDF, DOC, DOCX, XLS, XLSX, TXT, PNG, JPG, JPEG, GIF (Max: 10MB per file)
                    </p>
                  </div>
                </div>

                {/* ===== AUDIT INFORMATION SECTION ===== */}
                {isEditMode && (
                  <div style={{
                    backgroundColor: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px'
                  }}>
                    <h3 style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      marginBottom: '16px',
                      color: '#6b7280',
                      borderBottom: '2px solid #6b7280',
                      paddingBottom: '4px'
                    }}>
                      📅 Audit Information
                    </h3>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      {newRaidLog.created_date && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Created Date:</label>
                          <input
                            type="text"
                            value={formatDate(newRaidLog.created_date)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: '#f3f4f6'
                            }}
                            readOnly
                          />
                        </div>
                      )}

                      {newRaidLog.updated_date && (
                        <div>
                          <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Last Updated:</label>
                          <input
                            type="text"
                            value={formatDate(newRaidLog.updated_date)}
                            style={{
                              width: '100%',
                              padding: '8px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: '#f3f4f6'
                            }}
                            readOnly
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </form>
          )}
        </div>




{/* FROZEN Footer */}
        <div style={dialogStyles.footer}>
          {/* Error message */}
          {error && (
            <div style={{
              marginBottom: '15px',
              padding: '8px',
              borderRadius: '4px',
              backgroundColor: error.includes('successfully') ? '#f0f9ff' : '#fef2f2',
              color: error.includes('successfully') ? '#059669' : '#dc2626',
              border: error.includes('successfully') ? '1px solid #10b981' : '1px solid #ef4444'
            }}>
              {error}
            </div>
          )}

          {createSuccess ? (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px' }}>
              <button
                style={{
                  width: '100%',
                  padding: '8px 16px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
                onClick={() => {
                  onClose();
                }}
              >
                Close
              </button>
              {!isEditMode && !attachmentUploadFailed && (
                <button
                  style={{
                    width: '100%',
                    padding: '8px 16px',
                    backgroundColor: '#DBEAFE',
                    color: '#1E40AF',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  onClick={handleCreateNew}
                >
                  Create New
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {/* Left side - Close and Update buttons */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    backgroundColor: '#FFFDD0',
                    padding: '8px 16px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                  disabled={isLoading || isUploading}
                >
                  Close
                </button>

                <div
                  style={{
                    position: 'relative',
                    display: 'inline-block'
                  }}
                  onMouseEnter={(e) => {
                    if (!canCreateOrEditRaidLog) {
                      const tooltip = e.currentTarget.querySelector('[data-tooltip]');
                      if (tooltip) tooltip.style.visibility = 'visible';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!canCreateOrEditRaidLog) {
                      const tooltip = e.currentTarget.querySelector('[data-tooltip]');
                      if (tooltip) tooltip.style.visibility = 'hidden';
                    }
                  }}
                >
                  <button
                    type="button"
                    onClick={handleFormSubmit}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#C8F4DE',
                      color: 'black',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: !canCreateOrEditRaidLog ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      opacity: (isLoading || isUploading || (!isEditMode && attachmentUploadFailed) || !canCreateOrEditRaidLog) ? 0.3 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      pointerEvents: !canCreateOrEditRaidLog ? 'none' : 'auto'
                    }}
                    disabled={isLoading || isUploading || (!isEditMode && attachmentUploadFailed) || !canCreateOrEditRaidLog}
                  >
                    {(isLoading || isUploading) && (
                      <svg style={{
                        animation: 'spin 1s linear infinite',
                        marginLeft: '-4px',
                        marginRight: '8px',
                        width: '16px',
                        height: '16px'
                      }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    )}
                    {(isLoading || isUploading) ? (
                      isEditMode ? 'Updating...' : 'Creating...'
                    ) : (
                      isEditMode ? 'Update RAID Log' : 'Add New RAID Log'
                    )}
                  </button>

                  {/* Hover tooltip for disabled state */}
                  {!canCreateOrEditRaidLog && (
                    <div
                      data-tooltip
                      style={{
                        position: 'absolute',
                        left: '0',
                        bottom: '100%',
                        marginBottom: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#374151',
                        color: 'white',
                        fontSize: '12px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        visibility: 'hidden',
                        whiteSpace: 'nowrap',
                        zIndex: 20
                      }}
                    >
                      Insufficient permission
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: '16px',
                        width: 0,
                        height: 0,
                        borderLeft: '4px solid transparent',
                        borderRight: '4px solid transparent',
                        borderTop: '4px solid #374151'
                      }}></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right side - Manage Assignees button (only in edit mode) */}
              {isEditMode && newRaidLog.raid_id && (
                <div className="group relative">
                  <button
                    type="button"
                    onClick={handleManageAssigneesClick}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#f5d0ce',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <img
                      src="/icons/noun-team-6990022-644ebd.svg"
                      alt="Manage Assignees"
                      className="w-4 h-4"
                    />
                    Manage Assignees
                  </button>

                  {/* Tooltip */}
                  <div className="absolute right-0 bottom-full mb-2 px-3 py-2 bg-gray-200 text-gray-800 text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
                    Manage RAID log assignees
                    <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-200"></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!isEditMode && !createSuccess && (
            <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '16px', textAlign: 'center', margin: '16px 0 0 0' }}>
              You can continue adding more RAID logs. Click 'Close' when finished.
            </p>
          )}
        </div>













      </div>



      {/* Manage Assignees Dialog */}
      <ManageRaidLogAssignees
        isOpen={isManageAssigneesDialogOpen}
        onClose={() => setIsManageAssigneesDialogOpen(false)}
        corporateAccount={corporateAccount}
        projectId={currentProject}
        login_token={login_token}
        raidType={selectedRaidLogType}
        raidId={newRaidLog.raid_id}
        raidIdWithPrefix={selectedRaidLogIdWithPrefix}
        canEdit={true} // Assuming edit permissions if they can edit the RAID log
        userId={currentUser}
      />





    </div>
  );
};

export default CreateRaidLogDialog;